/**
 * Evacuation Order ↔ Incident / Community cross-link — pure resolution + gate (#206).
 *
 * The spatial join itself runs server-side (PostGIS); this module is the client
 * half of the #185 idiom: sort the matched rows, decide when a thread exists (the
 * contextual-render gate — render nothing when none does), and map a linked row to
 * the selection-bridge payload the COP opens + flies to. Kept pure (no fetch, no
 * React) so the link logic is unit-tested in isolation.
 */
import type { EvacOrderRef, LinkedIncidentRef, LinkedCommunityRef, EvacOrderLinks } from '@tsr/shared-types';
import type { BridgeSelection } from '../selectionBridge';

/** Register rank for ordering: a full Order outranks an Alert outranks anything
 *  else (the operationally-louder signal sorts first). */
function severityRank(severity: string | null): number {
  if (severity === 'order') return 0;
  if (severity === 'alert') return 1;
  return 2;
}

/** Order the evac orders shown on a community / incident panel: register first
 *  (order → alert → other), then by affected population (loudest impact), then
 *  name for stability. Pure + non-mutating. */
export function sortEvacOrders(orders: EvacOrderRef[]): EvacOrderRef[] {
  return [...orders].sort((a, b) => {
    const r = severityRank(a.severity) - severityRank(b.severity);
    if (r !== 0) return r;
    const pop = (b.population ?? 0) - (a.population ?? 0);
    if (pop !== 0) return pop;
    return (a.name ?? '').localeCompare(b.name ?? '');
  });
}

/** Order the incidents shown on an evac-order panel: open incidents first (the
 *  actionable ones), then most-recently-opened (incident_number is YYYY-NNNNN, so
 *  a descending string compare is recency). */
export function sortLinkedIncidents(incidents: LinkedIncidentRef[]): LinkedIncidentRef[] {
  return [...incidents].sort((a, b) => {
    if (a.status !== b.status) return a.status === 'open' ? -1 : 1;
    return b.incident_number.localeCompare(a.incident_number);
  });
}

/** Order the communities shown on an evac-order panel by name (stable, readable). */
export function sortLinkedCommunities(communities: LinkedCommunityRef[]): LinkedCommunityRef[] {
  return [...communities].sort((a, b) => a.name.localeCompare(b.name));
}

/** Contextual gate for the community/incident → evac-orders affordance. */
export function hasEvacOrderLinks(orders: EvacOrderRef[]): boolean {
  return orders.length > 0;
}

/** Contextual gate for the evac-order → incidents/communities affordance: render
 *  only when the order threads to at least one incident or community. */
export function hasOrderConnections(links: EvacOrderLinks): boolean {
  return links.incidents.length > 0 || links.communities.length > 0;
}

/** §4.30 — an incident carries an Evacuation Logistics surface once it has at least
 *  one linked community (data-driven entry). Mirrors EvacuationBody.hasEvacuation-
 *  Evidence, re-stated here so the pure module has no React import. */
export function incidentHasEvacuationEvidence(inc: LinkedIncidentRef): boolean {
  return inc.linked_community_count >= 1;
}

/** Only carry a finite centroid into a fly-to (the #185 guard — a bad coord must
 *  never poison the bridge selection). */
function withCentroid(base: BridgeSelection, lat: number, lon: number): BridgeSelection {
  if (Number.isFinite(lat)) base.lat = lat;
  if (Number.isFinite(lon)) base.lon = lon;
  return base;
}

/** Selection the COP opens for a linked evac order (select its panel + fly). */
export function evacOrderToSelection(order: EvacOrderRef): BridgeSelection {
  return withCentroid({ kind: 'evacuationOrder', id: order.id }, order.lat, order.lon);
}

/** Selection the COP flies to for a linked incident. */
export function incidentToSelection(inc: LinkedIncidentRef): BridgeSelection {
  return withCentroid({ kind: 'incident', id: inc.id }, inc.lat, inc.lon);
}

/** Selection that opens the incident-scoped Evacuation Logistics panel (§4.4). */
export function evacuationSurfaceSelection(inc: LinkedIncidentRef): BridgeSelection {
  return withCentroid({ kind: 'evacuation', id: inc.id }, inc.lat, inc.lon);
}

/** Selection the COP flies to for a linked community. */
export function communityToSelection(c: LinkedCommunityRef): BridgeSelection {
  return withCentroid({ kind: 'community', id: c.id }, c.lat, c.lon);
}
